/**
 * Stage 4: the Brief to Counsel.
 *
 * The second thing the app hands back at every court stage (`docs/STAGE4_SOP.md`
 * §2): the relevant facts with dates, the department's stand, the principal
 * legal grounds, and the points to watch at the hearing — ending with the Hindi
 * passages relied on and what the office must still confirm.
 *
 * It is drafted in the background, one section at a time, from the same papers
 * as the replies, and only after them. So the button stays on screen, greyed,
 * with the reason, until there are replies and they are not being drafted.
 *
 * **Read-only here, and the screen says so.** The Brief is edited in Word: the
 * download is the working copy. There is no Accept or Reject because there is
 * nothing in the app to change.
 *
 * A "to be verified by counsel" flag is the Brief's own: a provision or a
 * judgment named whose text is not in the papers. It is shown beside the point
 * it qualifies, never tidied away. What the checks by code found is listed too,
 * and never blocks a download — an error as something to check before the
 * Brief goes to counsel, a note as something to confirm against the original,
 * by the level the server gives each.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from '../api/client'
import type {
  BriefFactView,
  BriefFindingView,
  BriefGroundView,
  BriefPointView,
  BriefQuestionView,
  BriefTranslatedView,
  CounselBriefView,
  CourtPaperView,
} from '../api/client'
import { readDay } from '../lib/blocks'
import { FINDING_HEADS } from '../lib/court'
import { readDate } from '../lib/period'
import { ErrorEnvelope } from './bits'
import { ServiceNotRunning } from './court-draft'

/** The four sections, in the order they are drafted and printed. */
export const BRIEF_SECTIONS = [
  ['facts', 'Relevant facts with dates'],
  ['stand', 'The departmental stand'],
  ['grounds', 'Principal legal grounds'],
  ['hearing', 'Points for the hearing'],
] as const

const sectionTitle = (key: string | null | undefined): string =>
  BRIEF_SECTIONS.find(([k]) => k === key)?.[1] ?? ''

export interface BriefReading {
  /** What is happening, in plain words, without its closing mark. */
  headline: string
  count: string | null
  live: boolean
}

/** The Brief's progress, section by section — pinned in `counsel-brief.test.tsx`. */
export function readBriefProgress(
  brief: Pick<
    CounselBriefView,
    | 'state'
    | 'sections_done'
    | 'sections_total'
    | 'section'
    | 'stopped_reason'
    | 'service_unavailable'
  >,
): BriefReading {
  const total: number = brief.sections_total ?? BRIEF_SECTIONS.length
  const done: number = brief.sections_done ?? 0
  const count = `${done} of ${total} sections written`
  // Owed, and nothing is running to write it: "Drafting section 2…" would be
  // a sentence about work nobody is doing.
  if (brief.service_unavailable && (brief.state === 'queued' || brief.state === 'drafting')) {
    return { headline: 'Waiting for the drafting service', count, live: true }
  }
  switch (brief.state) {
    case 'queued':
      return { headline: 'Waiting to start', count: null, live: true }
    case 'drafting': {
      const title =
        sectionTitle(brief.section) || (BRIEF_SECTIONS[Math.min(done, total - 1)]?.[1] ?? '')
      return {
        headline: `Drafting section ${Math.min(done + 1, total)} of ${total}${title ? `: ${title}` : ''}`,
        count,
        live: true,
      }
    }
    case 'complete':
      return { headline: 'The Brief to Counsel is drafted', count, live: false }
    case 'stopped':
      return {
        headline: `Stopped: ${(brief.stopped_reason ?? '').trim().replace(/[.\s]+$/, '') || 'the Brief was not finished'}`,
        count,
        live: false,
      }
    default:
      return { headline: 'No Brief to Counsel has been drafted yet', count: null, live: false }
  }
}

/** The findings of one level, each under the section it was found in. */
function BriefFindings({
  findings,
  head,
  testId,
  note = false,
}: {
  findings: BriefFindingView[]
  head: string
  testId: string
  note?: boolean
}) {
  if (!findings.length) return null
  return (
    <div className={note ? 'd18-checks note' : 'd18-checks'} data-testid={testId}>
      <strong>{head}</strong>
      <ul>
        {findings.map((finding: BriefFindingView, index: number) => (
          <li key={index}>
            <span className="small muted">{sectionTitle(finding.section)}:</span>{' '}
            {finding.detail}
          </li>
        ))}
      </ul>
    </div>
  )
}

function Sources({ sources }: { sources: string[] | undefined }) {
  if (!sources?.length) return null
  return <span className="brief-sources small muted">Source: {sources.join('; ')}</span>
}

function Points({
  points,
  waiting,
}: {
  points: (BriefPointView | BriefGroundView)[]
  waiting: boolean
}) {
  if (!points.length) {
    return (
      <p className="small muted">
        {waiting ? 'Not written yet.' : 'This section was not drafted.'}
      </p>
    )
  }
  return (
    <ol className="brief-points">
      {points.map((item: BriefPointView | BriefGroundView, index: number) => {
        const verify: string = 'to_verify' in item ? (item.to_verify ?? '') : ''
        return (
          <li key={index}>
            <span>{item.point}</span>
            {verify ? (
              <span className="brief-verify small">
                <span className="chip warn">To be verified by counsel</span> {verify}
              </span>
            ) : null}
            <Sources sources={item.sources} />
          </li>
        )
      })}
    </ol>
  )
}

export function CounselBrief({
  caseId,
  documentType,
  repliesWritten,
  repliesDrafting,
  papers = [],
}: {
  caseId: string
  documentType: string
  /** How many replies stand on the open version. */
  repliesWritten: number
  /** True while the background job is still writing the replies. */
  repliesDrafting: boolean
  /** The papers of the draft, to name the paper a Hindi passage is in. */
  papers?: CourtPaperView[]
}) {
  const client = useQueryClient()
  const key = ['counsel-brief', caseId, documentType]
  const brief = useQuery({
    queryKey: key,
    queryFn: () => api.readCounselBrief(caseId, documentType),
    retry: false,
    // Asks again only while a Brief is being written.
    refetchInterval: (query) =>
      query.state.data?.state === 'queued' || query.state.data?.state === 'drafting'
        ? 3000
        : false,
  })
  const start = useMutation({
    mutationFn: () => api.draftCounselBrief(caseId, documentType),
    onSuccess: (fresh) => client.setQueryData(key, fresh),
  })

  const data: CounselBriefView | undefined = brief.data
  const state = data?.state ?? 'none'
  const reading = data ? readBriefProgress(data) : null
  const live = Boolean(reading?.live)
  const exists = Boolean(data && state !== 'none')
  const facts: BriefFactView[] = data?.facts ?? []
  const findings: BriefFindingView[] = data?.findings ?? []
  // A Brief drafted before levels were recorded carries none: its findings are
  // all shown as things to check, as they were.
  const errors = findings.filter((finding) => finding.level !== 'note')
  const findingNotes = findings.filter((finding) => finding.level === 'note')
  const translated: BriefTranslatedView[] = data?.translated ?? []
  const questions: BriefQuestionView[] = data?.questions ?? []
  const toConfirm: string[] = data?.office_to_confirm ?? []
  const notes: string[] = data?.notes ?? []
  // The Brief this one took the place of, most recent last — as the server has it.
  const replacedCount = data?.replaced?.length ?? 0
  const replaced = replacedCount ? data?.replaced?.[replacedCount - 1] : undefined
  const titleOf = (label: string) => papers.find((p) => p.label === label)?.title ?? label

  // Greyed, on screen, with the reason — never hidden. The server refuses the
  // same things in its own words; this says them before the click.
  const why = repliesDrafting
    ? 'The replies are still being drafted. The Brief is drafted after them, from the same papers.'
    : !repliesWritten
      ? 'There are no replies yet. Draft the replies first; the Brief is written beside them.'
      : live
        ? 'The Brief is being drafted now.'
        : ''

  return (
    <section className="d18-brief" aria-labelledby="brief-title" data-testid="counsel-brief">
      <h2 id="brief-title">Brief to Counsel</h2>
      <p className="small muted">
        The relevant facts with dates, the department’s stand, the principal legal grounds and
        the points for the hearing, drafted from the same papers as the replies. It cannot be
        edited here: download it and edit the Word file.
      </p>

      <div className="d18-brief-acts">
        <div className={why ? 'blocked inline unsettled' : 'row'}>
          <button
            type="button"
            className="btn"
            disabled={Boolean(why) || start.isPending}
            onClick={() => start.mutate()}
            data-testid="brief-draft"
          >
            {start.isPending
              ? 'Starting…'
              : state === 'stopped'
                ? 'Continue the Brief to Counsel'
                : state === 'complete'
                  ? 'Draft the Brief to Counsel again'
                  : 'Draft the Brief to Counsel'}
          </button>
          {why ? <span className="why">{why}</span> : null}
        </div>
        {exists ? (
          <div className="d18-downloads" aria-label="Download the Brief to Counsel">
            <span className="lbl">Download the Brief</span>
            <a className="btn" href={api.counselBriefPath(caseId, documentType, 'docx')}>
              Word
            </a>
            <a className="btn" href={api.counselBriefPath(caseId, documentType, 'pdf')}>
              PDF
            </a>
          </div>
        ) : (
          <div className="blocked inline unsettled" data-testid="brief-no-download">
            <button type="button" className="quiet" disabled>
              Word
            </button>
            <button type="button" className="quiet" disabled>
              PDF
            </button>
            <span className="why">Nothing to download until the Brief has been drafted.</span>
          </div>
        )}
      </div>
      <ErrorEnvelope error={start.error} />
      {brief.error ? (
        <>
          <p className="small">The Brief to Counsel could not be read from the server.</p>
          <ErrorEnvelope error={brief.error} />
        </>
      ) : null}

      {reading && exists ? (
        <div
          className={`court-progress ${state === 'stopped' || (live && data?.service_unavailable) ? 'stopped' : live ? 'working' : 'done'}`}
          role="status"
          aria-label="Progress of the Brief to Counsel"
          data-testid="brief-progress"
        >
          <p className="court-progress-head">
            <strong>
              {reading.headline}
              {reading.live ? '…' : '.'}
            </strong>
            {reading.count ? <span className="mono small"> {reading.count}</span> : null}
          </p>
          {live && data?.service_unavailable ? (
            /* Not a slow Brief: nothing is writing it. */
            <ServiceNotRunning
              said={data.service_unavailable}
              still="The sections already written are kept, and the Brief carries on when the service is running again. What is written can still be read below and downloaded."
            />
          ) : live ? (
            <p className="small muted">
              You can leave this page and come back. Each section appears below as it is
              finished.
            </p>
          ) : null}
          {state === 'stopped' ? (
            <p className="small">
              The sections already written are kept. Continuing drafts only the rest.
            </p>
          ) : null}
        </div>
      ) : null}
      {!exists && !brief.isLoading && !brief.error ? (
        <p data-testid="brief-none">
          No Brief to Counsel has been drafted for this document yet.
        </p>
      ) : null}
      {notes.map((note: string) => (
        <p key={note} className="small muted">
          {note}
        </p>
      ))}
      {replaced ? (
        /* Asking for the Brief again replaces the one that was here. That is
           said — when, and by whom — and not left to be noticed (rule 2). */
        <p className="small" role="status" data-testid="brief-replaced">
          An earlier Brief was replaced
          {replaced.replaced_at ? (
            <>
              {' '}
              on <span className="mono">{readDate(readDay(replaced.replaced_at))}</span>
            </>
          ) : null}
          {replaced.replaced_by ? ` by ${replaced.replaced_by}` : ''}.
          {replaced.reply_version
            ? ` It was drafted beside version ${replaced.reply_version} of the replies and stays on that version’s record.`
            : ' Its sections are kept on the record; they are not shown here.'}
          {replacedCount > 1
            ? ` ${replacedCount} earlier Briefs have been replaced in all.`
            : ''}
        </p>
      ) : null}

      {exists && data ? (
        <div className="brief-body">
          {papers.length ? (
            /* The Brief cites its sources by paper. What P1, P2 … are is said
               here, so a source can be found without leaving the tab. */
            <p className="small muted" data-testid="brief-papers">
              Sources are cited by paper:{' '}
              {papers.map((paper) => `${paper.label} ${paper.title}`).join(' · ')}.
            </p>
          ) : null}
          <h3>1. {BRIEF_SECTIONS[0][1]}</h3>
          {facts.length ? (
            <div className="table-scroll">
              <table className="brief-facts">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Event</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {facts.map((fact: BriefFactView, index: number) => (
                    <tr key={index}>
                      <td className="mono">{fact.date}</td>
                      <td>{fact.event}</td>
                      <td className="small muted">{fact.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="small muted">
              {live ? 'Not written yet.' : 'This section was not drafted.'}
            </p>
          )}

          <h3>2. {BRIEF_SECTIONS[1][1]}</h3>
          <Points points={data.stand ?? []} waiting={live} />
          <h3>3. {BRIEF_SECTIONS[2][1]}</h3>
          <Points points={data.grounds ?? []} waiting={live} />
          <h3>4. {BRIEF_SECTIONS[3][1]}</h3>
          <Points points={data.hearing ?? []} waiting={live} />

          <h3>Hindi passages relied on — confirm against the originals</h3>
          {translated.length ? (
            <ul>
              {translated.map((passage: BriefTranslatedView, index: number) => (
                <li key={index}>
                  <span className="mono small">
                    {titleOf(passage.paper)}
                    {passage.page ? `, page ${passage.page}` : ''}
                  </span>
                  {passage.about ? ` — ${passage.about}` : ''}
                </li>
              ))}
            </ul>
          ) : (
            <p className="small muted">None is listed.</p>
          )}

          <h3>What the office must still confirm</h3>
          {toConfirm.length || questions.length ? (
            <ul>
              {toConfirm.map((item: string) => (
                <li key={item}>{item}</li>
              ))}
              {questions.map((item: BriefQuestionView, index: number) => (
                <li key={`q-${index}`}>
                  <span className="small muted">
                    Asked on paragraph {item.paragraph}, not yet answered:
                  </span>{' '}
                  {item.question}
                </li>
              ))}
            </ul>
          ) : (
            <p className="small muted">Nothing is listed.</p>
          )}

          <BriefFindings
            findings={errors}
            head="Check before the Brief goes to counsel"
            testId="brief-findings"
          />
          <BriefFindings
            findings={findingNotes}
            head={FINDING_HEADS.note}
            testId="brief-notes"
            note
          />
          {findings.length ? (
            <p className="small muted">
              These are printed in the download too. They do not stop the download.
            </p>
          ) : null}

          {/* No cost here (18 Oct 2026): what the Brief's calls cost is recorded on
              the Brief for the team, never shown to the officer. */}
        </div>
      ) : null}
    </section>
  )
}
